import {
  Alert,
  Box,
  Button,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  Stack,
  Typography,
} from '@material-ui/core'
import CancelIcon from '@mui/icons-material/Cancel'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ReplayIcon from '@mui/icons-material/Replay'
import { useRef, useState } from 'react'
import {
  createUploadCancelSource,
  isUploadCancelled,
  saveMediaFiles,
} from 'src/api/media'
import { formatFileSize, validateMediaFile } from 'src/util/mediaConfig'
import { uploadToS3 } from 'src/util/uploadToS3'

const CONCURRENT_UPLOADS = 3

const STATUS_COLOR = {
  ready: 'text.secondary',
  uploading: 'info.main',
  success: 'success.main',
  failed: 'error.main',
  cancelled: 'warning.main',
  invalid: 'error.main',
}

let nextItemId = 1

/**
 * Bulk upload dialog content. Flow per file is the same one Practice Items
 * use: uploadToS3 (shared presign -> PUT into the flat `media/` folder), then
 * save the record with the file NAME only.
 * Files upload in parallel (max 3 at a time) with progress, cancel and retry.
 */
const MediaUploadDialog = ({ library, onClose, refetch }) => {
  const [items, setItems] = useState([])
  const [isBatchRunning, setIsBatchRunning] = useState(false)
  const [notice, setNotice] = useState('')
  const [isDragOver, setIsDragOver] = useState(false)
  const cancelSourcesRef = useRef({})
  const hasUploadedRef = useRef(false)

  const patchItem = (id, patch) =>
    setItems(prev => prev.map(item => (item.id === id ? { ...item, ...patch } : item)))

  const addFiles = fileList => {
    const files = Array.from(fileList || [])
    if (!files.length) return

    setNotice('')
    setItems(prev => {
      const next = [...prev]
      const skipped = []

      files.forEach(file => {
        const isDuplicate = next.some(
          item => item.file.name === file.name && item.file.size === file.size
        )
        if (isDuplicate) {
          skipped.push(file.name)
          return
        }
        const validationError = validateMediaFile(library, file)
        next.push({
          id: nextItemId++,
          file,
          status: validationError ? 'invalid' : 'ready',
          progress: 0,
          error: validationError || '',
        })
      })

      if (skipped.length) {
        setNotice(`Skipped duplicate file(s): ${skipped.join(', ')}`)
      }
      return next
    })
  }

  const uploadOne = async item => {
    const { id, file } = item
    const cancelSource = createUploadCancelSource()
    cancelSourcesRef.current[id] = cancelSource
    patchItem(id, { status: 'uploading', progress: 0, error: '' })

    try {
      await uploadToS3(file, {
        cancelToken: cancelSource.token,
        onProgress: progress => patchItem(id, { progress }),
      })

      await saveMediaFiles(library.key, [
        {
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
        },
      ])

      hasUploadedRef.current = true
      patchItem(id, { status: 'success', progress: 100 })
    } catch (err) {
      if (isUploadCancelled(err)) {
        patchItem(id, { status: 'cancelled', error: 'Upload cancelled' })
      } else {
        patchItem(id, {
          status: 'failed',
          error: err?.response?.data?.message || err?.message || 'Upload failed',
        })
      }
    } finally {
      delete cancelSourcesRef.current[id]
    }
  }

  const runQueue = async queue => {
    if (!queue.length) return
    setIsBatchRunning(true)
    const pending = [...queue]
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENT_UPLOADS, pending.length) }, async () => {
        while (pending.length) {
          await uploadOne(pending.shift())
        }
      })
    )
    setIsBatchRunning(false)
    if (hasUploadedRef.current) refetch()
  }

  const handleUploadAll = () => {
    setNotice('')
    runQueue(items.filter(item => item.status === 'ready'))
  }

  const handleRetry = item => runQueue([item])

  const handleCancel = id => cancelSourcesRef.current[id]?.cancel()

  const handleRemove = id => setItems(prev => prev.filter(item => item.id !== id))

  const handleClose = () => {
    // abort anything still in flight before closing
    Object.values(cancelSourcesRef.current).forEach(source => source.cancel())
    if (hasUploadedRef.current) refetch()
    onClose()
  }

  const readyCount = items.filter(item => item.status === 'ready').length
  const successCount = items.filter(item => item.status === 'success').length
  const failedCount = items.filter(item => ['failed', 'cancelled'].includes(item.status)).length

  return (
    <Box sx={{ m: 1 }}>
      <Stack spacing={2}>
        {/* Drag & drop zone (also opens the file browser on click) */}
        <Box
          component="label"
          onDragOver={e => {
            e.preventDefault()
            setIsDragOver(true)
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={e => {
            e.preventDefault()
            setIsDragOver(false)
            addFiles(e.dataTransfer.files)
          }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            p: 4,
            border: '2px dashed',
            borderColor: isDragOver ? 'primary.main' : 'divider',
            borderRadius: 2,
            backgroundColor: isDragOver ? 'action.hover' : 'background.default',
            cursor: 'pointer',
            textAlign: 'center',
          }}
        >
          <CloudUploadIcon color="primary" sx={{ fontSize: 42 }} />
          <Typography variant="body1">
            Drag & drop {library.label.toLowerCase()} here, or click to browse
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Allowed: {library.extensions.join(', ')} — up to {library.maxSizeLabel} per file
          </Typography>
          <input
            type="file"
            hidden
            multiple
            accept={library.extensions.map(ext => `.${ext}`).join(',')}
            onChange={e => {
              addFiles(e.target.files)
              e.target.value = ''
            }}
          />
        </Box>

        {notice && <Alert severity="warning">{notice}</Alert>}

        {items.length > 0 && (
          <List dense sx={{ maxHeight: 320, overflowY: 'auto', p: 0 }}>
            {items.map(item => (
              <ListItem
                key={item.id}
                disableGutters
                sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 1 }}
              >
                <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                  <Typography variant="body2" noWrap title={item.file.name}>
                    {item.file.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: STATUS_COLOR[item.status] }}>
                    {formatFileSize(item.file.size)}
                    {' — '}
                    {item.status === 'uploading' ? `${item.progress}%` : item.error || item.status}
                  </Typography>
                  {item.status === 'uploading' && (
                    <LinearProgress variant="determinate" value={item.progress} sx={{ mt: 0.5 }} />
                  )}
                </Box>

                {item.status === 'uploading' && (
                  <IconButton size="small" title="Cancel upload" onClick={() => handleCancel(item.id)}>
                    <CancelIcon fontSize="small" />
                  </IconButton>
                )}
                {['failed', 'cancelled'].includes(item.status) && (
                  <IconButton size="small" title="Retry upload" onClick={() => handleRetry(item)}>
                    <ReplayIcon fontSize="small" color="primary" />
                  </IconButton>
                )}
                {item.status !== 'uploading' && (
                  <IconButton size="small" title="Remove from list" onClick={() => handleRemove(item.id)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </ListItem>
            ))}
          </List>
        )}

        {(successCount > 0 || failedCount > 0) && !isBatchRunning && (
          <Alert severity={failedCount ? 'warning' : 'success'}>
            {successCount} uploaded successfully
            {failedCount ? `, ${failedCount} failed/cancelled — use retry to try again` : ''}.
          </Alert>
        )}

        <Stack direction="row" spacing={2}>
          <Button color="primary" onClick={handleClose} fullWidth size="large" variant="outlined">
            Close
          </Button>
          <Button
            color="primary"
            onClick={handleUploadAll}
            disabled={isBatchRunning || !readyCount}
            fullWidth
            size="large"
            variant="contained"
          >
            {isBatchRunning ? 'Uploading…' : `Upload ${readyCount || ''} File${readyCount === 1 ? '' : 's'}`}
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}

export default MediaUploadDialog
